import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { tipHTML } from './tip';
import { TooltipContext } from './context';

interface TipState {
  html: string;
  x: number;
  y: number;
  visible: boolean;
}

const OFFSET = 16;

/**
 * React replacement for the mockup's `window.BSTip` global (SPEC §6.1 / §9.6).
 * A single fixed-position bubble portalled to <body>, positioned with the same
 * measure-then-edge-flip math as chart-tip.jsx. Also runs the delegated
 * `[data-bstip]` handler so declarative segments (StackedBar etc.) work for free.
 *
 * SECURITY: bubble content is rendered via dangerouslySetInnerHTML. This is safe
 * because the HTML is produced only by `tipHTML`, which escapes every
 * data-derived field (title, row keys); the unescaped fields (value, rows.v,
 * note, sub) are filled exclusively by our own chart components with formatted
 * numbers and coloring spans — never with untrusted/user input. Callers that
 * interpolate data-derived strings into those fields must escape them first.
 */
export function TooltipProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<TipState>({ html: '', x: 0, y: 0, visible: false });
  const ref = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = useCallback((html: string, x: number, y: number) => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
    setState({ html, x, y, visible: true });
  }, []);

  const hide = useCallback(() => {
    // Bail out when already hidden so React skips the re-render + layout effect —
    // hide() fires on every global pointermove/scroll over empty space.
    setState((s) => (s.visible ? { ...s, visible: false } : s));
  }, []);

  // Measure-then-position with edge-flipping (line-for-line from chart-tip.jsx show()).
  useLayoutEffect(() => {
    const b = ref.current;
    if (!b) return;
    if (!state.visible) {
      b.classList.remove('show');
      return;
    }
    const w = b.offsetWidth;
    const h = b.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = state.x + OFFSET;
    let top = state.y + OFFSET;
    if (left + w + 6 > vw) left = state.x - w - OFFSET; // flip left
    if (left < 6) left = 6;
    if (top + h + 6 > vh) top = state.y - h - OFFSET; // flip above
    if (top < 6) top = 6;
    b.style.left = `${left}px`;
    b.style.top = `${top}px`;
    b.classList.add('show');
  }, [state]);

  // Delegated handler for declarative [data-bstip] elements; [data-bstip-skip]
  // elements manage their own tooltip imperatively and are left alone.
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const t = e.target as Element | null;
      if (!t || !t.closest) return;
      const managed = t.closest('[data-bstip],[data-bstip-skip]');
      if (!managed) {
        hide();
        return;
      }
      if (managed.hasAttribute('data-bstip-skip')) return;
      const raw = managed.getAttribute('data-bstip') ?? '';
      let html: string;
      try {
        html = tipHTML(JSON.parse(raw));
      } catch {
        html = tipHTML(raw);
      }
      show(html, e.clientX, e.clientY);
    };
    document.addEventListener('pointermove', onMove, true);
    document.addEventListener('pointerdown', onMove, true);
    // The pointer leaving the window fires none of the three below: `pointermove`
    // stops at the edge, there is no scroll, and `blur` only fires if focus
    // actually moves. Without this the bubble stays pinned over the page after a
    // flick off the top of the viewport (`chart-tip.jsx:144` hides here too).
    //
    // ON `documentElement` AND NOT `document … , true`. `pointerleave` does not
    // bubble, but it DOES capture, so a capturing listener on `document` sits in
    // the propagation path of every element's own pointerleave and would blank
    // the bubble each time the pointer crossed from one chart segment to the one
    // beside it. A listener bound directly to the root element fires only for
    // that element — which is exactly "the pointer left the window".
    document.documentElement.addEventListener('pointerleave', hide);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('blur', hide);
    return () => {
      document.removeEventListener('pointermove', onMove, true);
      document.removeEventListener('pointerdown', onMove, true);
      document.documentElement.removeEventListener('pointerleave', hide);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('blur', hide);
    };
  }, [show, hide]);

  return (
    <TooltipContext.Provider value={{ show, hide }}>
      {children}
      {createPortal(
        // Trusted content only — see the SECURITY note on this component.
        <div ref={ref} className="bs-tip" role="tooltip" dangerouslySetInnerHTML={{ __html: state.html }} />,
        document.body,
      )}
    </TooltipContext.Provider>
  );
}
